import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  Input,
  Select,
  Skeleton,
  Text,
  Textarea,
  useToast,
  FormControl,
  FormLabel,
  Divider,
} from "@chakra-ui/react";
import BreadCrumbMaker from "../../components/BreadCrumbMaker";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import RouteFlow from "../../components/RouteFlow";

const SERVER = import.meta.env.VITE_API_URL || "https://api.travelprym.in";

const BOOKING_STATUSES = {
  PENDING: 'Pending',
  CONFIRMED: 'Confirmed',
  CANCELLED: 'Cancelled',
  COMPLETED: 'Completed',
  INCOMING: 'Incoming',
  UPCOMING: 'Upcoming'
};

const TRIP_TYPES = {
  OW: 'OW',
  RT: 'RT',
  LOCAL: 'LOCAL',
  AIRPORT: 'AIRPORT'
};

const EditBooking = () => {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const token = localStorage.getItem("prima_session");
        if (!token) {
          toast({
            title: "Authentication required",
            status: "error",
            duration: 3000,
          });
          navigate("/bookings");
          return;
        }

        const res = await axios.get(`${SERVER}/management/booking?id=${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const bookingData = res.data.data;
        // Format datetime for input
        if (bookingData.dateTime) {
          bookingData.dateTime = moment(bookingData.dateTime).format('YYYY-MM-DDTHH:mm');
        }
        // Initialize locations from leadID if not directly available
        if (!bookingData.pickupLocation && bookingData.leadID?.fromLocation) {
          bookingData.pickupLocation = bookingData.leadID.fromLocation;
        }
        if (!bookingData.dropLocation && bookingData.leadID?.toLocation) {
          bookingData.dropLocation = bookingData.leadID.toLocation;
        }
        // Normalize status to proper case (handle UPPERCASE from old data)
        if (bookingData.status) {
          const normalizedStatus = Object.values(BOOKING_STATUSES).find(
            s => s.toUpperCase() === bookingData.status.toUpperCase()
          );
          if (normalizedStatus) {
            bookingData.status = normalizedStatus;
          }
        }
        setBooking(bookingData);
      } catch (err) {
        console.error(err);
        toast({
          title: "Error loading booking",
          description: err.response?.data?.message || "Failed to load booking details",
          status: "error",
          duration: 3000,
        });
        navigate("/bookings");
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();
  }, [id, navigate, toast]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem("prima_session");
      if (!token) {
        toast({
          title: "Authentication required",
          status: "error",
          duration: 3000,
        });
        return;
      }

      await axios.patch(
        `${SERVER}/management/booking/${id}`,
        {
          type: booking.type,
          pickupLocation: booking.pickupLocation,
          dropLocation: booking.dropLocation,
          dateTime: booking.dateTime,
          status: booking.status,
          notes: booking.notes,
          fareDetails: booking.fareDetails,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast({
        title: "Booking updated",
        description: "Booking details updated successfully",
        status: "success",
        duration: 3000,
      });

      navigate("/bookings");
    } catch (err) {
      console.error(err);
      toast({
        title: "Update failed",
        description: err.response?.data?.message || "Failed to update booking",
        status: "error",
        duration: 3000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box p={6}>
        <Skeleton height="20px" width="200px" mb={6} />
        <Skeleton height="40px" width="300px" mb={6} />
        <Box bg="white" p={6} rounded="md" shadow="sm">
          <Grid templateColumns="repeat(2, 1fr)" gap={6}>
            {Array.from({ length: 8 }).map((_, i) => (
              <GridItem key={i}>
                <Skeleton height="20px" width="120px" mb={2} />
                <Skeleton height="40px" />
              </GridItem>
            ))}
          </Grid>
        </Box>
      </Box>
    );
  }

  if (!booking) {
    return (
      <Box p={6}>
        <Text>Booking not found</Text>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <BreadCrumbMaker
        items={[
          { link: "/bookings", title: "Bookings" },
          { link: "#", title: "Edit Booking", on: true },
        ]}
      />

      <Heading size="lg" mb={6} mt={4}>
        Edit Booking
      </Heading>

      <Box bg="white" p={6} rounded="md" shadow="sm">
        {Array.isArray(booking?.leadID?.tripDetails?.stops) &&
          booking.leadID.tripDetails.stops.length > 0 && (
            <Box mb={6}>
              <RouteFlow
                from={booking.pickupLocation || booking.leadID?.fromLocation}
                to={booking.dropLocation || booking.leadID?.toLocation}
                stops={booking.leadID?.tripDetails?.stops || []}
                tripType={booking.type}
              />
            </Box>
          )}
        <form onSubmit={handleSubmit}>
          <Grid templateColumns="repeat(2, 1fr)" gap={6}>
            {/* Read-only fields */}
            <GridItem>
              <FormControl>
                <FormLabel>Booking ID</FormLabel>
                <Input value={booking.bookingID} isReadOnly bg="gray.50" />
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl>
                <FormLabel>Customer</FormLabel>
                <Input
                  value={booking.customer?.name || "-"}
                  isReadOnly
                  bg="gray.50"
                />
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl>
                <FormLabel>Mobile</FormLabel>
                <Input
                  value={booking.customer?.mobile || "-"}
                  isReadOnly
                  bg="gray.50"
                />
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl>
                <FormLabel>Vehicle Segment</FormLabel>
                <Input
                  value={booking.vehicleSegment?.segmentName || "-"}
                  isReadOnly
                  bg="gray.50"
                />
              </FormControl>
            </GridItem>

            <Divider gridColumn="span 2" />

            {/* Editable fields */}
            <GridItem>
              <FormControl isRequired>
                <FormLabel>Trip Type</FormLabel>
                <Select
                  value={booking.type}
                  onChange={(e) =>
                    setBooking({ ...booking, type: e.target.value })
                  }
                >
                  {Object.entries(TRIP_TYPES).map(([key, value]) => (
                    <option key={key} value={value}>
                      {value}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl isRequired>
                <FormLabel>Status</FormLabel>
                <Select
                  value={booking.status}
                  onChange={(e) =>
                    setBooking({ ...booking, status: e.target.value })
                  }
                >
                  {Object.values(BOOKING_STATUSES).map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl isRequired>
                <FormLabel>Pickup Location</FormLabel>
                <Input
                  value={booking.pickupLocation || ""}
                  onChange={(e) =>
                    setBooking({ ...booking, pickupLocation: e.target.value })
                  }
                  placeholder="Enter pickup location"
                />
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl isRequired>
                <FormLabel>Drop Location</FormLabel>
                <Input
                  value={booking.dropLocation || ""}
                  onChange={(e) =>
                    setBooking({ ...booking, dropLocation: e.target.value })
                  }
                  placeholder="Enter drop location"
                />
              </FormControl>
            </GridItem>

            <GridItem colSpan={2}>
              <FormControl isRequired>
                <FormLabel>Date & Time</FormLabel>
                <Input
                  type="datetime-local"
                  value={booking.dateTime}
                  onChange={(e) =>
                    setBooking({ ...booking, dateTime: e.target.value })
                  }
                />
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl>
                <FormLabel>Total Fare (₹)</FormLabel>
                <Input
                  type="number"
                  value={booking.fareDetails?.total || 0}
                  onChange={(e) =>
                    setBooking({
                      ...booking,
                      fareDetails: {
                        ...booking.fareDetails,
                        total: Number(e.target.value),
                      },
                    })
                  }
                  min={0}
                  step={0.01}
                />
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl>
                <FormLabel>Base Fare (₹)</FormLabel>
                <Input
                  type="number"
                  value={booking.fareDetails?.baseFare || 0}
                  onChange={(e) =>
                    setBooking({
                      ...booking,
                      fareDetails: {
                        ...booking.fareDetails,
                        baseFare: Number(e.target.value),
                      },
                    })
                  }
                  min={0}
                  step={0.01}
                />
              </FormControl>
            </GridItem>

            <GridItem colSpan={2}>
              <FormControl>
                <FormLabel>Notes</FormLabel>
                <Textarea
                  value={booking.notes || ""}
                  onChange={(e) =>
                    setBooking({ ...booking, notes: e.target.value })
                  }
                  placeholder="Enter any additional notes"
                  rows={4}
                />
              </FormControl>
            </GridItem>
          </Grid>

          <Flex mt={8} gap={4}>
            <Button
              type="submit"
              colorScheme="blue"
              isLoading={submitting}
              loadingText="Updating..."
            >
              Update Booking
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/bookings")}
              isDisabled={submitting}
            >
              Cancel
            </Button>
          </Flex>
        </form>
      </Box>
    </Box>
  );
};

export default EditBooking;
